import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { FILTER_NAMES } from "../constants/filters";
import { cssMainSelector } from "../lib/cssSelectors";
import MediaContainer from "./MediaContainer";
import ContractTermFilterContainer from "../containers/filters/ContractTermFilterContainer";
import CurrentProviderFilterContainer from "../containers/filters/CurrentProviderFilterContainer";
import DataCreditsFilterContainer from "../containers/filters/DataCreditsFilterContainer";
import DataSpeedGenFilterContainer from "../containers/filters/DataSpeedGenFilterContainer";
import DataSpeedDownFilterContainer from "../containers/filters/DataSpeedDownFilterContainer";
import MonthlyFeeFilterContainer from "../containers/filters/MonthlyFeeFilterContainer";
import OfferExtraGroupFilterContainer from "../containers/filters/OfferExtraGroupFilterContainer";
import PriceFilterContainer from "../containers/filters/PriceFilterContainer";
import ProviderFilterContainer from "../containers/filters/ProviderFilterContainer";
import VoiceCreditsFilterContainer from "../containers/filters/VoiceCreditsFilterContainer";
import MaterialIcon from "./MaterialIcon";
import { withTranslation } from "react-i18next";
import BrandFilterContainer from "../containers/filters/BrandFilterContainer";
import PhoneOperatingSystemFilterContainer from "../containers/filters/PhoneOperatingSystemFilterContainer";
import { objectValues } from "../lib/utils";

const Container = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    width: 220px;
  }
`;

const mainFiltersPadding = css `padding: ${p => p.theme.spacing.none} ${p => p.theme.spacing.md}`;
const MainFilters = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    background-color: ${p => p.theme.filter.mainFiltersBackgroundColor};
    border-color: ${p => p.theme.filter.mainFiltersBorderColor};
    border-radius: ${p => p.theme.generic.borderRadius}
      ${p => p.theme.generic.borderRadius} 0 0;
    border-style: solid;
    border-width: 1px;
    ${mainFiltersPadding};
  }
`;

const MainFilter = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    padding: ${p => p.theme.spacing.md} 0;
  }
`;

const secondaryFiltersPadding = css `padding: ${p => p.theme.spacing.none} ${p => p.theme.spacing.md}`;
const secondaryFiltersBorderRadius = css `border-radius: 0 0 ${p => p.theme.generic.borderRadius} ${p => p.theme.generic.borderRadius}`;
const SecondaryFilters = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    background-color: ${p => p.theme.filter.secondaryFiltersBackgroundColor};
    border-color: ${p => p.theme.filter.secondaryFiltersBorderColor};
    ${secondaryFiltersBorderRadius};
    border-style: solid;
    border-width: 0 1px 1px 1px;
    ${secondaryFiltersPadding};
    
    & > div {
      border-bottom: 1px solid ${p =>
        p.theme.filter.secondaryFiltersBorderColor};
    }
    
    & > div:empty {
      display: none;
    }
     
    & > div:last-child {
      border-bottom: none;
    }
  }
`;

const SecondaryFilter = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    padding: ${p => p.theme.spacing.md} 0;
  }
`;

const ResetWrapper = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    color: ${p => p.theme.filter.resetFiltersColor};
    cursor: pointer;
    font-size: ${p => p.theme.font.sizeSm};
    padding-top: ${p => p.theme.spacing.md};
    text-align: center;
    user-select: none;

    * {
      color: ${p => p.theme.filter.resetFiltersColor};
      cursor: pointer;
      font-size: ${p => p.theme.font.sizeSm};
    }
  }
`;

class FilterPanel extends PureComponent {
  render() {
    const { onResetClick, t } = this.props;

    return (
      <Container>
        <MediaContainer>
          <MainFilters>{this.mainFiltersList()}</MainFilters>
          <SecondaryFilters>{this.secondaryFiltersList()}</SecondaryFilters>
          <ResetWrapper onClick={onResetClick}>
            <MaterialIcon>autorenew</MaterialIcon> {t("resetFilters")}
          </ResetWrapper>
        </MediaContainer>
      </Container>
    );
  }

  mainFiltersList() {
    const { clientConfig, offerType } = this.props;

    const mainFilters =
      clientConfig.widgetOptions[offerType].filters.mainFilters;

    const items = [];

    for (const mainFilter of mainFilters) {
      if (mainFilter === FILTER_NAMES.dataCredits) {
        items.push(
          <MainFilter key={FILTER_NAMES.dataCredits}>
            <DataCreditsFilterContainer />
          </MainFilter>
        );
      } else if (mainFilter === FILTER_NAMES.monthlyFee) {
        items.push(
          <MainFilter key={FILTER_NAMES.monthlyFee}>
            <MonthlyFeeFilterContainer />
          </MainFilter>
        );
      } else if (mainFilter === FILTER_NAMES.price) {
        items.push(
          <MainFilter key={FILTER_NAMES.price}>
            <PriceFilterContainer />
          </MainFilter>
        );
      } else if (mainFilter === FILTER_NAMES.voiceCredits) {
        items.push(
          <MainFilter key={FILTER_NAMES.voiceCredits}>
            <VoiceCreditsFilterContainer />
          </MainFilter>
        );
      }
    }

    return items;
  }

  secondaryFiltersList() {
    const {
      clientConfig,
      offerExtraGroups,
      offerExtraIds,
      offerType
    } = this.props;

    let secondaryFilters =
      clientConfig.widgetOptions[offerType].filters.secondaryFilters;

    const items = [];

    for (const secondaryFilter of secondaryFilters) {
      if (
        typeof secondaryFilter !== "object" &&
        secondaryFilter === FILTER_NAMES.brand
      ) {
        items.push(
          <SecondaryFilter key={FILTER_NAMES.brand}>
            <BrandFilterContainer />
          </SecondaryFilter>
        );
      } else if (
        typeof secondaryFilter !== "object" &&
        secondaryFilter === FILTER_NAMES.contractTermInMonths
      ) {
        items.push(
          <SecondaryFilter key={FILTER_NAMES.contractTermInMonths}>
            <ContractTermFilterContainer />
          </SecondaryFilter>
        );
      } else if (
        typeof secondaryFilter !== "object" &&
        secondaryFilter === FILTER_NAMES.currentProvider
      ) {
        items.push(
          <SecondaryFilter key={FILTER_NAMES.currentProvider}>
            <CurrentProviderFilterContainer />
          </SecondaryFilter>
        );
      } else if (
        typeof secondaryFilter !== "object" &&
        secondaryFilter === FILTER_NAMES.dataSpeedDown
      ) {
        items.push(
          <SecondaryFilter key={FILTER_NAMES.dataSpeedDown}>
            <DataSpeedDownFilterContainer />
          </SecondaryFilter>
        );
      } else if (
        typeof secondaryFilter !== "object" &&
        secondaryFilter === FILTER_NAMES.dataSpeedGen
      ) {
        items.push(
          <SecondaryFilter key={FILTER_NAMES.dataSpeedGen}>
            <DataSpeedGenFilterContainer />
          </SecondaryFilter>
        );
      } else if (
        typeof secondaryFilter !== "object" &&
        secondaryFilter === FILTER_NAMES.monthlyFee
      ) {
        items.push(
          <SecondaryFilter key={FILTER_NAMES.monthlyFee}>
            <MonthlyFeeFilterContainer />
          </SecondaryFilter>
        );
      } else if (
        typeof secondaryFilter === "object" &&
        secondaryFilter.name === FILTER_NAMES.offerExtraGroups
      ) {
        // first get the offerExtraGroups in the correct order
        let offerExtraGroupsReordered = [];
        if (
          secondaryFilter.order !== undefined &&
          Array.isArray(secondaryFilter.order) &&
          secondaryFilter.order.length > 0
        ) {
          let offerExtraGroupsUnused = { ...offerExtraGroups };
          for (const offerExtraId of secondaryFilter.order) {
            offerExtraGroupsReordered.unshift(offerExtraGroups[offerExtraId]);
            delete offerExtraGroupsUnused[offerExtraId];
          }

          const offerExtraGroupsUnusedReordered = objectValues(
            offerExtraGroupsUnused
          ).sort((a, b) =>
            (a["frontendTitle"] || "").localeCompare(b["frontendTitle"] || "")
          );
          offerExtraGroupsReordered.push(...offerExtraGroupsUnusedReordered);
        } else {
          offerExtraGroupsReordered = objectValues(
            offerExtraGroups
          ).sort((a, b) =>
            (a["frontendTitle"] || "").localeCompare(b["frontendTitle"] || "")
          );
        }

        for (const offerExtraGroup of offerExtraGroupsReordered) {
          const offerExtraIdsInGroup = offerExtraGroup.offerExtras.map(
            oE => oE.id
          );
          const filterOfferExtraIds = offerExtraIds.filter(
            oEId => offerExtraIdsInGroup.indexOf(oEId) >= 0
          );
          if (
            filterOfferExtraIds.length > 0 &&
            offerExtraGroup.isFrontendFilter
          ) {
            items.push(
              <SecondaryFilter
                key={`${FILTER_NAMES.offerExtraGroup}${offerExtraGroup.id}`}
              >
                <OfferExtraGroupFilterContainer
                  offerExtraGroup={offerExtraGroup}
                />
              </SecondaryFilter>
            );
          }
        }
      } else if (
        typeof secondaryFilter !== "object" &&
        secondaryFilter === FILTER_NAMES.phoneOperatingSystem
      ) {
        items.push(
          <SecondaryFilter key={FILTER_NAMES.phoneOperatingSystem}>
            <PhoneOperatingSystemFilterContainer />
          </SecondaryFilter>
        );
      } else if (
        typeof secondaryFilter !== "object" &&
        secondaryFilter === FILTER_NAMES.providers
      ) {
        items.push(
          <SecondaryFilter key={FILTER_NAMES.providers}>
            <ProviderFilterContainer />
          </SecondaryFilter>
        );
      }
    }

    return items;
  }
}

FilterPanel.propTypes = {
  clientConfig: PropTypes.object.isRequired,
  offerExtraGroups: PropTypes.object.isRequired,
  offerExtraIds: PropTypes.array.isRequired,
  offerType: PropTypes.string.isRequired,
  onResetClick: PropTypes.func.isRequired
};

export default withTranslation(["filters"])(FilterPanel);
